/**
 * Competition / winner / prize-delivery status (epic-6).
 *
 * Shows the campaign lifecycle status, whether a winner has been verified, and
 * whether the prize has been delivered — plus a link to the prize-handover
 * delivery video when one exists. Each step is a labelled row with a state pill;
 * colour is paired with a text label + icon so it is never colour-only.
 */
import { Icon } from '../../components/Icon';
import type { FunnelWinner } from '../../lib/api';

interface Props {
  status: string;
  winner: FunnelWinner;
}

function StatePill({ done, label }: { done: boolean; label: string }) {
  return (
    <span
      className={[
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium',
        done
          ? 'bg-primary-50 text-primary-700'
          : 'bg-surface-muted text-ink-500',
      ].join(' ')}
    >
      <Icon
        name={done ? 'check' : 'clock'}
        className="h-3.5 w-3.5"
      />
      {label}
    </span>
  );
}

export function WinnerStatus({ status, winner }: Props) {
  const prettyStatus = status.replace(/_/g, ' ');

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-sm text-ink-700">Campaign status</span>
        <span className="rounded-full bg-aurora-soft px-3 py-1 text-xs font-semibold capitalize text-ink">
          {prettyStatus}
        </span>
      </div>

      <div className="flex items-center justify-between">
        <span className="flex items-center gap-2 text-sm text-ink-700">
          <Icon name="trophy" className="h-4 w-4 text-ink-500" />
          Winner verified
        </span>
        <StatePill done={winner.verified} label={winner.verified ? 'Verified' : 'Pending'} />
      </div>

      <div className="flex items-center justify-between">
        <span className="flex items-center gap-2 text-sm text-ink-700">
          <Icon name="gift" className="h-4 w-4 text-ink-500" />
          Prize delivered
        </span>
        <StatePill done={winner.delivered} label={winner.delivered ? 'Delivered' : 'Pending'} />
      </div>

      {winner.delivery_video_url ? (
        <a
          href={winner.delivery_video_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-line px-3 py-2 text-sm font-medium text-primary-700 transition-colors duration-200 hover:bg-primary-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <Icon name="video" className="h-4 w-4" />
          Watch prize-handover video
          <span className="sr-only"> (opens in a new tab)</span>
        </a>
      ) : (
        <p className="text-xs text-ink-500">
          The prize-handover video will appear here once delivery is recorded.
        </p>
      )}
    </div>
  );
}

export default WinnerStatus;
